import type { SubmissionEarningsKind } from './resolveSubmissionEarningsDisplay';
import { isAccrualWindowClosed } from './isAccrualWindowClosed';

export type SubmissionEarningsHint =
  | { kind: 'none' }
  | { kind: 'limitReached' }
  | { kind: 'accrualWindowClosed'; windowHours: number }
  | { kind: 'missedThresholdInWindow'; threshold: number; windowHours: number }
  | { kind: 'viewsRemainingToEarn'; count: number; threshold: number }
  | { kind: 'budgetExhausted' }
  | { kind: 'remainingToLimit'; remainingCents: number };

export function resolveSubmissionEarningsHint({
  earningsKind,
  earnedCents,
  maxPayoutPerClipCents,
  viewsRemainingToThreshold,
  minViewsThreshold,
  remainingBudgetCents,
  submittedAt,
  windowHours,
  now,
}: {
  earningsKind: SubmissionEarningsKind;
  earnedCents: number;
  maxPayoutPerClipCents?: number | null;
  viewsRemainingToThreshold: number;
  minViewsThreshold?: number | null;
  remainingBudgetCents?: number | null;
  submittedAt?: string | null;
  windowHours?: number | null;
  now: Date;
}): SubmissionEarningsHint {
  if (earningsKind === 'none') return { kind: 'none' };

  const perClipCap = maxPayoutPerClipCents != null && maxPayoutPerClipCents > 0
    ? maxPayoutPerClipCents
    : null;
  if (perClipCap && earnedCents >= perClipCap) {
    return { kind: 'limitReached' };
  }

  const threshold = minViewsThreshold || 0;
  const windowClosed = !!(
    windowHours
    && windowHours > 0
    && isAccrualWindowClosed({ submittedAt, windowHours, now })
  );

  if (windowClosed && earnedCents === 0 && viewsRemainingToThreshold > 0) {
    return {
      kind: 'missedThresholdInWindow',
      threshold: threshold || viewsRemainingToThreshold,
      windowHours: windowHours as number,
    };
  }

  if (windowClosed) {
    return { kind: 'accrualWindowClosed', windowHours: windowHours as number };
  }

  if (viewsRemainingToThreshold > 0) {
    return {
      kind: 'viewsRemainingToEarn',
      count: viewsRemainingToThreshold,
      threshold: threshold || viewsRemainingToThreshold,
    };
  }

  const budgetGone = typeof remainingBudgetCents === 'number'
    && Number.isFinite(remainingBudgetCents)
    && remainingBudgetCents <= 0;
  if (earnedCents === 0 && budgetGone) {
    return { kind: 'budgetExhausted' };
  }

  if (perClipCap && earnedCents > 0) {
    return {
      kind: 'remainingToLimit',
      remainingCents: Math.max(0, perClipCap - earnedCents),
    };
  }

  return { kind: 'none' };
}
